// extracted to have an easier tranition into the italian release
// Todo: € symbol is used but hardcoded, needs to be adapted if England is added to the system
import * as Yup from 'yup'
import { NationalContractReferenceLevelDto, ContractChangeValidationListFragment, EmployeeLevelEnum } from '../../../../types'
import { TFunction } from 'i18next'
import { bonusChangeValidation, BonusChangeValidationType } from './bonusChangeValidation'
import {
  getDateByMonthForValidationWithBackdating,
  UndefinedContractChangeValuesForGivenMonth,
  getUndefinedContractChangeValuesForGivenMonth,
  getDateByMonthForValidationWithoutBackdating,
} from './validationHelpers'
import { salaryChangeValidation, SalaryChangeValidationType } from './salaryChangeValidation'
import { salaryAndBonusTransferValidation, SalaryAndBonusTransferValidationType } from './salaryAndBonusTransferValidation'
import { employeeLevelChangeValidation, EmployeeLevelChangeValidationType } from './employeeLevelChangeValidation'
import { NationalContractLevelChangeValidationType, nationalContractLevelChangeValidation } from './nationalContractLevelValidation'

// Obsolete method as validation is moved to BE
export const getItalianContractChangeFormValidationSchema = (
  nationalContractReference: NationalContractReferenceLevelDto[],
  contractChangeValidationList: ContractChangeValidationListFragment,
  employeeLevels: EmployeeLevelEnum[],
  t: TFunction,
): Yup.ObjectSchema => {
  return Yup.object().shape({
    changes: Yup.object().shape(
      {
        salary: Yup.number()
          .transform((value) => (isNaN(value) ? undefined : value))
          .test({
            name: 'salaryChange',
            // arrow functions cannot be used here because of different behavior in this binding
            test: function () {
              const context: any = this.options.context
              const { salary, bonus, employeeLevel, nationalReferenceLevel } = context.changes

              // default case at least one field needs to be filled out -> add the dropdown
              if (salary === undefined && employeeLevel === undefined && bonus === undefined && nationalReferenceLevel === undefined) {
                return this.createError({
                  message: t('formError.conditionalRequired', {
                    fieldNames: `${t('appraisalMeeting.contract.changes.form.salary')},
                ${t('appraisalMeeting.contract.employeeLevel')},
                ${t('appraisalMeeting.contract.bonus')},
                ${t('appraisalMeeting.contract.nationalReferenceLevel')}`,
                  }),
                  path: 'changes.salary',
                })
              }

              if (salary === undefined && nationalReferenceLevel === undefined && bonus === undefined) {
                return true
              }

              // validFrom is not defined so validation for validFrom will fail. It's safe to exit.
              if (context.validFrom === undefined) {
                return true
              }

              const validFrom: Date = getDateByMonthForValidationWithBackdating(context.validFrom)

              if (salary !== undefined) {
                const salaryValidationResult: SalaryChangeValidationType = salaryChangeValidation(
                  validFrom,
                  salary,
                  contractChangeValidationList,
                )

                if (!salaryValidationResult.valid) {
                  return this.createError({
                    message: t('formError.increaseSalaryUpForPeriodOfTime', {
                      fieldName: t(`appraisalMeeting.contract.changes.form.salary`),
                      minAmount: salaryValidationResult.minimalValidValue,
                      currency: '€',
                    }),
                    path: 'changes.salary',
                  })
                }
              }
              const contractChangeValuesForGivenMonth: UndefinedContractChangeValuesForGivenMonth = getUndefinedContractChangeValuesForGivenMonth(
                validFrom,
                contractChangeValidationList,
              )
              if (bonus !== undefined && bonus < contractChangeValuesForGivenMonth.bonus) {
                // we only call salaryAndBonusTransferValidationResult when the bonus is lower then the bonus in the past change
                const salaryAndBonusTransferValidationResult: SalaryAndBonusTransferValidationType = salaryAndBonusTransferValidation(
                  validFrom,
                  salary,
                  bonus,
                  contractChangeValidationList,
                )
                if (!salaryAndBonusTransferValidationResult.valid && salaryAndBonusTransferValidationResult.futurePackageDecrease) {
                  return this.createError({
                    message: t('formError.salaryPlusBonusDecreasesValue', {
                      fieldNames: `${t('appraisalMeeting.contract.changes.form.salary')}, ${t(
                        'appraisalMeeting.contract.changes.form.bonus',
                      )}`,
                      minAmount: salaryAndBonusTransferValidationResult.minimalValidValue,
                      currency: '€',
                    }),
                    path: 'changes.salary',
                  })
                }

                if (!salaryAndBonusTransferValidationResult.valid && !salaryAndBonusTransferValidationResult.futurePackageDecrease) {
                  return this.createError({
                    message: t('formError.salaryPlusBonusDecreasesFuturePackage', {
                      fieldNames: `${t('appraisalMeeting.contract.changes.form.salary')}, ${t(
                        'appraisalMeeting.contract.changes.form.bonus',
                      )}`,
                      minAmount: salaryAndBonusTransferValidationResult.minimalValidValue,
                      currency: '€',
                    }),
                    path: 'changes.salary',
                  })
                }
              }

              // Use selected salary or active one for given time period (based on validFrom)
              const usableSalary: number = salary !== undefined ? salary : contractChangeValuesForGivenMonth.salary

              // Use selected national contract level or active one for given time period (based on validFrom)
              const usableNationalContractLevel = nationalReferenceLevel
                ? nationalReferenceLevel
                : contractChangeValuesForGivenMonth.nationalContractLevel

              // Salary has to be higher than minimal salary defined for current national contract level
              if (nationalReferenceLevel) {
                const nc = nationalContractReference.find((nc) => nc.level === usableNationalContractLevel)
                if (nc && usableSalary < nc.minSalary) {
                  return this.createError({
                    message: t('formError.increaseSalaryUpToNationalContract', {
                      fieldName: t(`appraisalMeeting.contract.changes.form.salary`),
                      minAmount: nc.minSalary,
                      currency: '€',
                    }),
                    path: 'changes.salary',
                  })
                }
              }

              return true
            },
          }),
        bonus: Yup.number()
          .transform((value) => (isNaN(value) ? undefined : value))
          .test({
            name: 'TargetBonusTest',
            test: function () {
              const context: any = this.options.context
              const { bonus, employeeLevel, nationalReferenceLevel } = context.changes
              let { salary } = context.changes

              if (salary === undefined && employeeLevel === undefined && bonus === undefined && nationalReferenceLevel === undefined) {
                return this.createError({
                  message: t('formError.conditionalRequired', {
                    fieldNames: `${t('appraisalMeeting.contract.changes.form.salary')},
                ${t('appraisalMeeting.contract.employeeLevel')},
                ${t('appraisalMeeting.contract.bonus')},
                ${t('appraisalMeeting.contract.nationalReferenceLevel')}`,
                  }),
                  path: 'changes.bonus',
                })
              }

              if (context.validFrom !== undefined) {
                const validFrom: Date = getDateByMonthForValidationWithBackdating(context.validFrom)
                const validFromWithoutBackdate: Date = getDateByMonthForValidationWithoutBackdating(context.validFrom)
                const contractChangeValuesForGivenMonth: UndefinedContractChangeValuesForGivenMonth = getUndefinedContractChangeValuesForGivenMonth(
                  validFrom,
                  contractChangeValidationList,
                )

                if (bonus !== undefined && bonus < contractChangeValuesForGivenMonth.bonus) {
                  // we only call salaryAndBonusTransferValidationResult when the bonus is lower then the bonus in the past change
                  if (salary === undefined) {
                    salary = contractChangeValuesForGivenMonth.salary
                  }

                  const salaryAndBonusTransferValidationResult: SalaryAndBonusTransferValidationType = salaryAndBonusTransferValidation(
                    validFrom,
                    salary,
                    bonus,
                    contractChangeValidationList,
                  )
                  if (!salaryAndBonusTransferValidationResult.valid && salaryAndBonusTransferValidationResult.futurePackageDecrease) {
                    return this.createError({
                      message: t('formError.salaryPlusBonusDecreasesValue', {
                        fieldNames: `${t('appraisalMeeting.contract.changes.form.salary')}, ${t(
                          'appraisalMeeting.contract.changes.form.bonus',
                        )}`,
                        minAmount: salaryAndBonusTransferValidationResult.minimalValidValue,
                        currency: '€',
                      }),
                      path: 'changes.bonus',
                    })
                  }

                  if (!salaryAndBonusTransferValidationResult.valid && !salaryAndBonusTransferValidationResult.futurePackageDecrease) {
                    return this.createError({
                      message: t('formError.salaryPlusBonusDecreasesFuturePackage', {
                        fieldNames: `${t('appraisalMeeting.contract.changes.form.salary')}, ${t(
                          'appraisalMeeting.contract.changes.form.bonus',
                        )}`,
                        minAmount: salaryAndBonusTransferValidationResult.minimalValidValue,
                        currency: '€',
                      }),
                      path: 'changes.bonus',
                    })
                  }
                }

                if (bonus !== undefined && bonus >= contractChangeValuesForGivenMonth.bonus) {
                  const bonusChangeValidationResult: BonusChangeValidationType = bonusChangeValidation(
                    validFromWithoutBackdate,
                    bonus,
                    contractChangeValidationList,
                  )
                  if (!bonusChangeValidationResult.valid) {
                    return this.createError({
                      message: t('formError.increaseBonusUpForPeriodOfTime', {
                        fieldName: t(`appraisalMeeting.contract.changes.form.bonus`),
                        minAmount: bonusChangeValidationResult.minimalValidValue,
                        currency: '€',
                      }),
                      path: 'changes.bonus',
                    })
                  }
                }
              }

              return true
            },
          }),

        employeeLevel: Yup.string()
          .transform((value) => (isNaN(value) ? undefined : value))
          .test({
            name: 'EmployeeLevelTest',
            test: function () {
              const context: any = this.options.context
              const { salary, bonus, employeeLevel, nationalReferenceLevel } = context.changes

              if (salary === undefined && employeeLevel === undefined && bonus === undefined && nationalReferenceLevel === undefined) {
                return this.createError({
                  message: t('formError.conditionalRequired', {
                    fieldNames: `${t('appraisalMeeting.contract.changes.form.salary')},
                ${t('appraisalMeeting.contract.employeeLevel')},
                ${t('appraisalMeeting.contract.bonus')},
                ${t('appraisalMeeting.contract.nationalReferenceLevel')}`,
                  }),
                  path: 'changes.employeeLevel',
                })
              }

              // validFrom is not defined so validation for validForm will fail. It's safe to exit.
              if (context.validFrom === undefined) {
                return true
              }

              const validFrom: Date = getDateByMonthForValidationWithBackdating(context.validFrom)

              if (employeeLevel !== undefined) {
                const employeeLevelValidationResult: EmployeeLevelChangeValidationType = employeeLevelChangeValidation(
                  validFrom,
                  employeeLevel,
                  contractChangeValidationList,
                  employeeLevels,
                )

                if (!employeeLevelValidationResult.valid) {
                  return this.createError({
                    message: t('formError.increaseEmployeeLevelUpForPeriodOfTime', {
                      fieldName: t(`appraisalMeeting.contract.employeeLevel`),
                      minAmount: employeeLevelValidationResult.minimalValidValue,
                    }),
                    path: 'changes.employeeLevel',
                  })
                }
              }

              return true
            },
          }),

        nationalReferenceLevel: Yup.string()
          .transform((value) => (isNaN(value) ? undefined : value))
          .test({
            name: 'EmployeeLevelTest',
            test: function () {
              const context: any = this.options.context
              const { salary, bonus, employeeLevel, nationalReferenceLevel } = context.changes

              if (salary === undefined && employeeLevel === undefined && bonus === undefined && nationalReferenceLevel === undefined) {
                return this.createError({
                  message: t('formError.conditionalRequired', {
                    fieldNames: `${t('appraisalMeeting.contract.changes.form.salary')},
                ${t('appraisalMeeting.contract.employeeLevel')},
                ${t('appraisalMeeting.contract.bonus')},
                ${t('appraisalMeeting.contract.nationalReferenceLevel')}`,
                  }),
                  path: 'changes.nationalReferenceLevel',
                })
              }

              // validFrom is not defined so validation for vaildForm will fail. It's safe to exit.
              if (context.validFrom === undefined) {
                return true
              }

              const validFrom: Date = getDateByMonthForValidationWithBackdating(context.validFrom)

              if (nationalReferenceLevel !== undefined) {
                const nationalContractLevelValidationResult: NationalContractLevelChangeValidationType = nationalContractLevelChangeValidation(
                  validFrom,
                  nationalReferenceLevel,
                  nationalContractReference,
                  contractChangeValidationList,
                )

                if (!nationalContractLevelValidationResult.valid) {
                  return this.createError({
                    message: t('formError.increaseNationalContractEmployeeLevelUpForPeriodOfTime', {
                      fieldName: t(`appraisalMeeting.contract.nationalContractLevel`),
                    }),
                    path: 'changes.nationalReferenceLevel',
                  })
                }
              }

              return true
            },
          }),
      },
      // the following list of fields is used in the noSoftEdges parameter that is barely documented
      // https://gitmemory.com/issue/jquense/yup/720/564591045
      // it is basically a hack to force a certain sorting of fields to avoid cyclical dependencies
      // it is unclear if it is needed here, it will remain and needs to be analyzed in a different ticket
      [
        ['bonus', 'salary'],
        ['employeeLevel', 'salary'],
        ['employeeLevel', 'bonus'],
      ],
    ),

    validFrom: Yup.string().required(t('formError.required', { fieldName: t(`appraisalMeeting.contract.changes.form.validFrom`) })),
  })
}
